/*
  R-565 : carrousel de réels, lecture au survol (modèle relevé sur quickz-it.fr le 01/10).
  Ordinateur (pointeur fin avec survol) : la vidéo démarre au survol, sans son, en boucle,
  s'arrête et revient au début à la sortie du pointeur. Un clic ouvre les contrôles natifs.
  Tactile : un toucher lance la lecture (sans son, contrôles natifs), une seule vidéo à la fois.
  Chargement : aucun octet vidéo au chargement ni au défilement. L'élément vidéo est préparé
  (preload="none") quand la carte approche de l'écran ; dès que le pointeur entre dans le
  carrousel, les cartes visibles passent en preload="metadata" pour un départ immédiat ;
  le reste du fichier n'arrive qu'à la lecture. Rendu 720p (le 1080p d'origine en secours).
  prefers-reduced-motion : aucune lecture au survol, le clic et le clavier restent actifs.
*/
const HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');
const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
/** @type {HTMLElement | null} */
let current = null;

const hoverAllowed = () => HOVER.matches && !REDUCE.matches;

/** @param {HTMLElement} host */
function ensureVideo(host) {
  if (host.czVideo) return host.czVideo;
  const src = host.dataset.czSrcHd;
  const box = host.querySelector('[data-ref="deferred-media-video"]');
  if (!src || !box) return null;
  const video = document.createElement('video');
  video.muted = true;
  video.defaultMuted = true;
  video.setAttribute('muted', '');
  video.playsInline = true;
  video.setAttribute('playsinline', '');
  video.loop = true;
  video.preload = 'none';
  video.tabIndex = -1;
  video.src = src;
  /* Si un rendu léger manque, on retombe sur le fichier d'origine. */
  video.addEventListener('error', () => {
    const fb = host.dataset.czSrcFb;
    if (fb && video.getAttribute('src') !== fb) {
      const wasPlaying = current === host;
      video.src = fb;
      if (wasPlaying) video.play().catch(() => {});
    }
  });
  video.addEventListener('playing', () => host.classList.add('cz-reel--playing'));
  video.addEventListener('pause', () => host.classList.remove('cz-reel--playing'));
  box.appendChild(video);
  host.czVideo = video;
  return video;
}

/** @param {HTMLElement} host */
function play(host) {
  const video = ensureVideo(host);
  if (!video) return;
  if (current && current !== host) stop(current, true);
  current = host;
  const p = video.play();
  if (p && p.catch) p.catch(() => {});
}

/** @param {HTMLElement} host @param {boolean} reset */
function stop(host, reset) {
  const video = host.czVideo;
  host.classList.remove('cz-reel--engaged');
  if (!video) return;
  video.pause();
  video.controls = false;
  if (reset) {
    try { video.currentTime = 0; } catch (e) {}
  }
  if (current === host) current = null;
}

/** Clic, toucher ou clavier : lecture avec contrôles natifs, ou pause si déjà engagée. */
function activate(host) {
  const video = ensureVideo(host);
  if (!video) return;
  if (host.classList.contains('cz-reel--engaged') && !video.paused) {
    video.pause();
    return;
  }
  host.classList.add('cz-reel--engaged');
  video.controls = true;
  play(host);
}

const nearObserver = 'IntersectionObserver' in window
  ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        nearObserver.unobserve(entry.target);
        if (hoverAllowed()) ensureVideo(/** @type {HTMLElement} */ (entry.target));
      });
    }, { rootMargin: '300px 0px', threshold: 0 })
  : null;

const viewObserver = 'IntersectionObserver' in window
  ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const host = /** @type {HTMLElement} */ (entry.target);
        if (!entry.isIntersecting && host.czVideo && !host.czVideo.paused) stop(host, false);
      });
    }, { threshold: 0 })
  : null;

/** Le pointeur entre dans le carrousel : on prépare les cartes visibles. */
function warm(section) {
  if (!hoverAllowed()) return;
  section.querySelectorAll('[data-cz-reel]').forEach((el) => {
    const host = /** @type {HTMLElement} */ (el);
    const r = host.getBoundingClientRect();
    if (r.right < 0 || r.left > window.innerWidth || r.bottom < 0 || r.top > window.innerHeight) return;
    const video = ensureVideo(host);
    if (video && video.preload === 'none') video.preload = 'metadata';
  });
}

/** @param {HTMLElement} host */
function init(host) {
  const section = host.closest('.shopify-section') || host.parentElement;
  if (section && !section.dataset.czReelWarm) {
    section.dataset.czReelWarm = '1';
    section.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') warm(section); });
  }

  if (host.dataset.czReelReady) return;
  host.dataset.czReelReady = '1';

  const poster = host.querySelector('[data-ref="deferred-media-poster"]');
  if (poster) {
    poster.setAttribute('role', 'button');
    poster.setAttribute('tabindex', '0');
    poster.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        activate(host);
      }
    });
  }

  host.addEventListener('pointerenter', (e) => {
    if (e.pointerType !== 'mouse' || !hoverAllowed()) return;
    play(host);
  });

  host.addEventListener('pointerleave', (e) => {
    if (e.pointerType !== 'mouse') return;
    if (host.classList.contains('cz-reel--engaged')) return;
    stop(host, true);
  });

  /* Capture : on prend la main avant le lecteur d'origine (deferred-media.js),
     sinon il injecterait une seconde vidéo 1080p par-dessus. */
  host.addEventListener('click', (e) => {
    const target = /** @type {Element} */ (e.target);
    if (host.classList.contains('cz-reel--engaged') && target.closest('video')) return;
    e.preventDefault();
    e.stopPropagation();
    activate(host);
  }, true);

  nearObserver?.observe(host);
  viewObserver?.observe(host);
}

function scan(root = document) {
  root.querySelectorAll('[data-cz-reel]').forEach((el) => init(/** @type {HTMLElement} */ (el)));
}

scan();
document.addEventListener('shopify:section:load', (e) => scan(/** @type {Element} */ (e.target)));
